import React, { useState } from "react";
import {
  Box, Stack, Typography, TextField,
  Checkbox, CircularProgress, Tooltip, IconButton, Button,
} from "@mui/material";
import AssigneePicker from "../../../../components/AssigneePicker";
import AssigneeSelect from "../../../../components/AssigneeSelect";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { PRIORITY_OPTIONS, TASK_STATUS_OPTIONS } from "../../constants/recurrenceConstants";
import { fixGrammarAndSpell } from "../../../../services/ai.service";

const stripHtml = (html) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.innerText || div.textContent || "";
};

const T = {
  border: "#E2E8F0", text: "#0F172A", textSub: "#475569", textMuted: "#94A3B8",
  primary: "#0D9488", primaryDark: "#0F766E", primaryLight: "#F0FDFA", surface: "#FFFFFF",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const ChipSelector = ({ label, required, options, value, onChange, error }) => (
  <Box>
    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: T.textSub, mb: 1 }}>
      {label} {required && <span style={{ color: "#EF4444" }}>*</span>}
    </Typography>
    <Stack direction="row" gap={1} flexWrap="wrap">
      {options.map(({ value: v, label: l, color, bg }) => {
        const active = value === v;
        return (
          <Box
            key={v}
            onClick={() => onChange(v)}
            sx={{
              px: "16px", py: "7px",
              borderRadius: "9px",
              border: `1.5px solid ${active ? color : T.border}`,
              bgcolor: active ? bg : T.surface,
              color:   active ? color : T.textSub,
              fontWeight: active ? 700 : 500,
              fontSize: "13px",
              cursor: "pointer",
              transition: "all 0.15s",
              "&:hover": { borderColor: color, color },
              userSelect: "none",
            }}
          >
            {l}
          </Box>
        );
      })}
    </Stack>
    {error && <Typography sx={{ fontSize: "11px", color: "#EF4444", mt: 0.5 }}>{error}</Typography>}
  </Box>
);

// ─── StepTaskDetails ──────────────────────────────────────────────────────────
const StepTaskDetails = ({
  values, set, errors,
  supportValue, onSupportChange,
  onAssigneesChange,
  acknowledgedByValue, onAcknowledgedByChange,
}) => {
  const [titleFixing, setTitleFixing] = useState(false);
  const [titleFixed,  setTitleFixed]  = useState(false);
  const [descFixing,  setDescFixing]  = useState(false);
  const [descFixed,   setDescFixed]   = useState(false);

  const handleTitleFix = async () => {
    if (titleFixing || !values.title?.trim()) return;
    setTitleFixing(true);
    try {
      const res = await fixGrammarAndSpell(values.title.trim());
      const corrected = res?.correctedText;
      if (corrected) { set("title", corrected); setTitleFixed(true); setTimeout(() => setTitleFixed(false), 2000); }
    } catch (_) {}
    finally { setTitleFixing(false); }
  };

  const handleDescFix = async () => {
    if (descFixing || !values.description?.trim()) return;
    setDescFixing(true);
    try {
      const res = await fixGrammarAndSpell(stripHtml(values.description).trim() || values.description.trim());
      const corrected = res?.correctedText;
      if (corrected) { set("description", corrected); setDescFixed(true); setTimeout(() => setDescFixed(false), 2000); }
    } catch (_) {}
    finally { setDescFixing(false); }
  };

  return (
    <Stack spacing={3}>

      {/* Title */}
      <Box>
        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: T.textSub, mb: 0.8 }}>
          Task title <span style={{ color: "#EF4444" }}>*</span>
        </Typography>
        <TextField
          fullWidth size="small"
          placeholder="e.g. Weekly team standup"
          value={values.title}
          onChange={(e) => set("title", e.target.value)}
          error={!!errors.title}
          helperText={errors.title}
          sx={{ "& .MuiInputBase-input": { fontSize: "14px", paddingRight: "36px" } }}
          InputProps={{
            endAdornment: (
              <Tooltip title="Fix spelling & grammar (AI)">
                <IconButton
                  size="small"
                  onClick={handleTitleFix}
                  disabled={titleFixing || !values.title?.trim()}
                  sx={{
                    width: 26, height: 26, borderRadius: "6px",
                    color: titleFixed ? "#16A34A" : "#7C3AED",
                    bgcolor: titleFixed ? "#F0FDF4" : "#FAF5FF",
                    border: `1px solid ${titleFixed ? "#86EFAC" : "#DDD6FE"}`,
                    transition: "all 0.2s",
                    "&:hover": { bgcolor: "#EDE9FE", color: "#6D28D9" },
                    "&.Mui-disabled": { opacity: 0.4 },
                  }}
                >
                  {titleFixing
                    ? <CircularProgress size={11} sx={{ color: "#7C3AED" }} />
                    : <AutoFixHighIcon sx={{ fontSize: 13 }} />
                  }
                </IconButton>
              </Tooltip>
            ),
          }}
        />
      </Box>

      {/* Description */}
      <Box>
        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: T.textSub, mb: 0.8 }}>
          Description
        </Typography>
        <TextField
          fullWidth multiline minRows={3} size="small"
          placeholder="What does this recurring task involve?"
          value={values.description}
          onChange={(e) => set("description", e.target.value)}
          sx={{ "& .MuiInputBase-input": { fontSize: "13px", paddingTop: "10px", paddingLeft: "10px" } }}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.6 }}>
          <Tooltip title="Fix spelling & grammar (AI)">
            <Button
              size="small"
              onClick={handleDescFix}
              disabled={descFixing || !values.description?.trim()}
              startIcon={
                descFixing
                  ? <CircularProgress size={11} sx={{ color: "#7C3AED" }} />
                  : <AutoFixHighIcon sx={{ fontSize: "13px !important" }} />
              }
              sx={{
                textTransform: "none", fontSize: "12px", fontWeight: 600,
                px: 1.6, py: 0.5, borderRadius: "8px",
                color: descFixed ? "#16A34A" : "#7C3AED",
                bgcolor: descFixed ? "#F0FDF4" : "#FAF5FF",
                border: `1.5px solid ${descFixed ? "#86EFAC" : "#DDD6FE"}`,
                transition: "all 0.2s",
                "&:hover": { bgcolor: "#EDE9FE", color: "#6D28D9", border: "1.5px solid #C4B5FD" },
                "&.Mui-disabled": { opacity: 0.4 },
              }}
            >
              {descFixing ? "Fixing…" : descFixed ? "Fixed!" : "Fix spelling & grammar"}
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Priority */}
      <ChipSelector
        label="Priority" required
        options={PRIORITY_OPTIONS}
        value={values.priority}
        onChange={(v) => set("priority", v)}
        error={errors.priority}
      />

      {/* Default Status */}
      <ChipSelector
        label="Default status for each task"
        options={TASK_STATUS_OPTIONS}
        value={values.defaultStatus}
        onChange={(v) => set("defaultStatus", v)}
      />

      {/* Assignees */}
      <Box>
        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: T.textSub, mb: 0.8 }}>
          Assignees
        </Typography>
        <AssigneeSelect
          roles="HR,PROJECT MANAGER"
          value={(values.assignees || []).map((a) => a._id)}
          preloaded={values.assignees || []}
          onChange={(_ids, options) => onAssigneesChange(options)}
          placeholder="Search members…"
          emptyLabel="Add assignees"
        />
        <Typography sx={{ fontSize: "11px", color: T.textMuted, mt: 0.6 }}>
          If left empty, tasks are created as Not Assigned
        </Typography>
        <Box
          onClick={() => set("privateAssignees", !values.privateAssignees)}
          sx={{ display: "flex", alignItems: "center", gap: 0.3, mt: 0.8, cursor: "pointer" }}
        >
          <Checkbox
            size="small"
            checked={!!values.privateAssignees}
            onChange={(e) => set("privateAssignees", e.target.checked)}
            sx={{ p: 0.2, color: T.textMuted, "&.Mui-checked": { color: T.primary } }}
          />
          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: values.privateAssignees ? T.primary : T.textSub, userSelect: "none" }}>
            Keep assignee information private
          </Typography>
        </Box>
        {/* Reads the current state rather than describing only the ON case:
            privacy is the default now, so the helper has to explain both. */}
        <Typography sx={{ fontSize: "11px", color: T.textMuted, mt: 0.4, ml: 3.4 }}>
          {values.privateAssignees
            ? "Each assignee only sees their own line on every generated task — untick to let them see each other"
            : "Assignees will see each other on every generated task"}
        </Typography>
      </Box>

      {/* Support */}
      <Box>
        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: T.textSub, mb: 0.8 }}>
          Support
        </Typography>
        <AssigneePicker
          roles="HR,PROJECT MANAGER"
          value={supportValue?._id || ""}
          preloadedOption={supportValue}
          onChange={(_id, option) => onSupportChange(option)}
          placeholder="Search and assign a support member"
        />
        <Typography sx={{ fontSize: "11px", color: T.textMuted, mt: 0.6 }}>
          If left empty, tasks are created without a support member
        </Typography>
      </Box>

      {/* Acknowledged By */}
      <Box>
        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: T.textSub, mb: 0.8 }}>
          Acknowledged By
        </Typography>
        <AssigneePicker
          roles="HR,PROJECT MANAGER"
          value={acknowledgedByValue?._id || ""}
          preloadedOption={acknowledgedByValue}
          onChange={(_id, option) => onAcknowledgedByChange(option)}
          placeholder="Search and assign an acknowledger"
        />
        <Typography sx={{ fontSize: "11px", color: T.textMuted, mt: 0.6 }}>
          If left empty, tasks are created without an acknowledged by person
        </Typography>
      </Box>

    </Stack>
  );
};

export default StepTaskDetails;
